/**
 * CR104: Merchant ID Normalization
 * Deterministic algorithm to generate canonical merchant_id keys
 */

/**
 * Normalize a string to a valid merchant_id
 * 
 * Algorithm:
 * 1. Trim and lowercase
 * 2. Replace "&" with "and"
 * 3. Remove apostrophes and quotes
 * 4. Replace non-alphanumeric with hyphens
 * 5. Collapse multiple hyphens
 * 6. Trim leading/trailing hyphens
 * 7. Max length 64 chars
 * 
 * Examples:
 * - "Walmart (GetMyBonus)" → "walmart-getmybonus"
 * - "Toys R Us" → "toys-r-us"
 * - "A1 Cash + Carry" → "a1-cash-carry"
 * - "Loblaws®" → "loblaws"
 */
export function normalizeMerchantId(input: string): string {
  if (!input) return "";
  
  let s = input.trim().toLowerCase();
  
  // Replace & with "and"
  s = s.replace(/&/g, "and");
  
  // Remove apostrophes and quotes: ' " ' " "
  s = s.replace(/['"''""]/g, "");
  
  // Replace any non-alphanumeric characters with hyphens
  s = s.replace(/[^a-z0-9]+/g, "-");
  
  // Collapse multiple hyphens
  s = s.replace(/-+/g, "-");
  
  // Trim leading/trailing hyphens
  s = s.replace(/^-+|-+$/g, "");
  
  // Max length 64 chars
  if (s.length > 64) {
    s = s.substring(0, 64);
    // Clean up if we cut in middle of hyphen
    s = s.replace(/-+$/g, "");
  }
  
  return s;
}

/**
 * Validate that a merchant ID is valid after normalization
 */
export function isValidMerchantId(input: string): boolean {
  const normalized = normalizeMerchantId(input);
  return normalized.length > 0;
}

/**
 * Get validation error message for merchant ID
 */
export function getMerchantIdError(input: string): string | null {
  if (!input.trim()) {
    return "Merchant ID is required";
  }
  
  const normalized = normalizeMerchantId(input);
  if (!normalized) {
    return "Invalid merchant ID - must contain at least one alphanumeric character";
  }
  
  return null;
}
